export const SCHEDULING_WIZARD_STEPS = [
  'audit-schedule',
  'site-location',
  'accomodation-transportation',
  'auditor-assignment',
  'review-confirm',
];

export const SCHEDULING_WIZARD_DRAFT_VERSION = 1;
export const SCHEDULING_WIZARD_DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const hasText = (value) => String(value ?? '').trim().length > 0;
const isPositiveNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0;
};
const isIsoDate = (value) => /^\d{4}-\d{2}-\d{2}$/.test(String(value || '').trim());

const hasValidDateRange = (row) => {
  if (!isIsoDate(row?.startDate) || !isIsoDate(row?.endDate)) return false;
  return String(row.startDate) <= String(row.endDate);
};

const getMandayRows = (row) => (Array.isArray(row?.mandayDetails) ? row.mandayDetails : [])
  .filter((detail) => hasText(detail?.date) || hasText(detail?.mandays));

const validateAuditSchedule = (data) => {
  const schedules = Array.isArray(data?.auditSchedules) ? data.auditSchedules : [];
  const errors = [];
  if (schedules.length === 0) return ['Add at least one audit schedule.'];

  schedules.forEach((schedule, index) => {
    const mandayRows = getMandayRows(schedule);
    const hasDates = mandayRows.some((detail) => isIsoDate(detail?.date)) || hasValidDateRange(schedule);
    if (!hasDates) errors.push(`Schedule ${index + 1} requires at least one valid audit date.`);
    if (mandayRows.length > 1 && mandayRows.some((detail) => !isIsoDate(detail?.date) || !isPositiveNumber(detail?.mandays))) {
      errors.push(`Schedule ${index + 1} requires a date and positive mandays for every date row.`);
    }
  });
  return errors;
};

const validateSites = (data) => {
  const sites = Array.isArray(data?.siteLocations) ? data.siteLocations : [];
  if (sites.length === 0) return ['Add at least one site location.'];
  return sites.flatMap((site, index) => {
    const errors = [];
    if (!hasText(site?.siteName)) errors.push(`Site ${index + 1} requires a name.`);
    if (!hasText(site?.address)) errors.push(`Site ${index + 1} requires an address.`);
    return errors;
  });
};

const validateAccommodation = (data) => {
  const rows = Array.isArray(data?.accommodations) ? data.accommodations : [];
  return rows.flatMap((row, index) => {
    if (row?.providedByClient) return [];
    const errors = [];
    if (!hasText(row?.purpose)) errors.push(`Accommodation ${index + 1} requires a purpose.`);
    if (!hasText(row?.scheduledAuditorId)) errors.push(`Accommodation ${index + 1} requires an auditor.`);
    return errors;
  });
};

const validateAuditors = (data) => {
  const schedules = Array.isArray(data?.auditSchedules) ? data.auditSchedules : [];
  if (schedules.length === 0) return ['Add at least one auditor assignment.'];
  return schedules.flatMap((schedule, index) => {
    const errors = [];
    if (!hasText(schedule?.employeeId)) errors.push(`Auditor ${index + 1} requires a staff assignment.`);
    if (!hasText(schedule?.role)) errors.push(`Auditor ${index + 1} requires a project role.`);
    return errors;
  });
};

const validateConfirmation = (data) => {
  const prerequisiteErrors = [
    ...validateAuditSchedule(data),
    ...validateSites(data),
    ...validateAccommodation(data),
    ...validateAuditors(data),
  ];
  if (prerequisiteErrors.length > 0) return prerequisiteErrors;
  const schedules = Array.isArray(data?.auditSchedules) ? data.auditSchedules : [];
  return schedules.some((schedule) => String(schedule?.scheduleStatus || '').trim().toLowerCase() !== 'confirmed')
    ? ['Confirm every auditor schedule to complete scheduling.']
    : [];
};

export const validateSchedulingWizardStep = (stepId, data = {}) => {
  switch (stepId) {
    case 'audit-schedule': return validateAuditSchedule(data);
    case 'site-location': return validateSites(data);
    case 'accomodation-transportation': return validateAccommodation(data);
    case 'auditor-assignment': return validateAuditors(data);
    case 'review-confirm': return validateConfirmation(data);
    default: return ['Unknown scheduling step.'];
  }
};

export const isSchedulingWizardStepComplete = (stepId, data = {}) => (
  validateSchedulingWizardStep(stepId, data).length === 0
);

export const deriveSchedulingWizardProgress = (data = {}) => SCHEDULING_WIZARD_STEPS.reduce((result, stepId) => ({
  ...result,
  [stepId]: isSchedulingWizardStepComplete(stepId, data),
}), {});

export const getFirstIncompleteSchedulingStep = (data = {}) => (
  SCHEDULING_WIZARD_STEPS.find((stepId) => !isSchedulingWizardStepComplete(stepId, data))
  || SCHEDULING_WIZARD_STEPS[SCHEDULING_WIZARD_STEPS.length - 1]
);

// The Scheduling wizard's own "Audit Scope" dropdown can select a different
// scope than whichever outer accordion card happens to be expanded (e.g. the
// Surveillance 1st card is expanded while Recertification is picked in the
// wizard) -- the Mandays limit must always follow the SELECTED scope, never
// the expanded card, or the validation warning compares against the wrong
// scope's mandays entirely. Only fall back to the expanded card / first stage
// when no scope is selected at all.
export const resolveScopeMandaysLimit = (stages = [], selectedScopeKey = null, { expandedStages = {} } = {}) => {
  const bySelection = selectedScopeKey
    ? stages.find((stage) => stage.key === selectedScopeKey)
    : null;
  const activeStage = bySelection
    || stages.find((stage) => expandedStages[stage.key])
    || stages[0]
    || null;
  const raw = Number(activeStage?.mandays);
  return Number.isFinite(raw) && raw > 0 ? raw : null;
};

export const buildSchedulingWizardDraftKey = (projectId, scopeKey) => {
  if (!hasText(projectId) || !hasText(scopeKey)) return null;
  return `audit-scheduling-wizard:v${SCHEDULING_WIZARD_DRAFT_VERSION}:${encodeURIComponent(String(projectId))}:${encodeURIComponent(String(scopeKey))}`;
};

const resolveStorage = (storage) => {
  if (storage) return storage;
  if (typeof window === 'undefined') return null;
  return window.localStorage;
};

const normalizeDraft = (raw, now = Date.now()) => {
  if (!raw || raw.version !== SCHEDULING_WIZARD_DRAFT_VERSION) return null;
  if (!Number.isFinite(Number(raw.savedAt))) return null;
  if (now - Number(raw.savedAt) > SCHEDULING_WIZARD_DRAFT_TTL_MS) return null;
  const activeStep = SCHEDULING_WIZARD_STEPS.includes(raw.activeStep)
    ? raw.activeStep
    : 'audit-schedule';
  return {
    version: SCHEDULING_WIZARD_DRAFT_VERSION,
    savedAt: Number(raw.savedAt),
    activeStep,
    data: {
      siteLocations: Array.isArray(raw?.data?.siteLocations) ? raw.data.siteLocations : [],
      auditSchedules: Array.isArray(raw?.data?.auditSchedules) ? raw.data.auditSchedules : [],
      accommodations: Array.isArray(raw?.data?.accommodations) ? raw.data.accommodations : [],
    },
  };
};

export const loadSchedulingWizardDraft = ({ projectId, scopeKey, storage, now = Date.now() }) => {
  const key = buildSchedulingWizardDraftKey(projectId, scopeKey);
  const target = resolveStorage(storage);
  if (!key || !target) return null;
  try {
    const serialized = target.getItem(key);
    if (!serialized) return null;
    const draft = normalizeDraft(JSON.parse(serialized), now);
    if (!draft) target.removeItem(key);
    return draft;
  } catch (_) {
    try { target.removeItem(key); } catch (ignored) { /* Storage may be unavailable. */ }
    return null;
  }
};

export const saveSchedulingWizardDraft = ({ projectId, scopeKey, activeStep, data, storage, now = Date.now() }) => {
  const key = buildSchedulingWizardDraftKey(projectId, scopeKey);
  const target = resolveStorage(storage);
  if (!key || !target) return false;
  const draft = normalizeDraft({
    version: SCHEDULING_WIZARD_DRAFT_VERSION,
    savedAt: now,
    activeStep,
    data,
  }, now);
  if (!draft) return false;
  try {
    target.setItem(key, JSON.stringify(draft));
    return true;
  } catch (_) {
    return false;
  }
};

export const clearSchedulingWizardDraft = ({ projectId, scopeKey, storage }) => {
  const key = buildSchedulingWizardDraftKey(projectId, scopeKey);
  const target = resolveStorage(storage);
  if (!key || !target) return false;
  try {
    target.removeItem(key);
    return true;
  } catch (_) {
    return false;
  }
};
